import React from 'react';
import clsx from 'clsx';

import { Divider, Spinner } from '@auspost/postmaster-react/raw';

import Infotip from 'App/common/Tooltip/Infotip/infotip';
import ParcelLockerIcon from 'assets/parcel-locker.svg';
import ParcelCollectIcon from 'assets/post-office.svg';
import TruckIcon from 'assets/truck.svg';
import { AddressType, Milestone } from 'models/shipments';
import { selectCalculatedEdd } from 'store/deliveryEstimate';
import { useAppSelector } from 'store/store';
import {
  formatTimeWindow,
  getCalculatedDeliveryDateText,
  getEddRangeText,
  getEddText,
  isToday,
} from 'utils/date.utils';

import { CommonClass } from '../../../../config/track-config';
import { ELoadingStatus, useLocationByDpid } from '../../hooks/locations.hook';
import { useCurrentArticle } from '../../hooks/shipment.hooks';
import InlineEdd from './InlineEdd/InlineEdd';
import { TRACKING_PANEL_EDD_TEST_ID, TRACKING_PANEL_EDD_TEXT_TEST_ID } from './Edd.test-ids';
import { tooltipContent } from './Edd.tooltip.config';

import styles from './Edd.module.scss';

const Edd: React.FC = () => {
  const {
    address,
    deliveredByDateISO,
    estimatedDeliveryDateRange: eddRange,
    estimatedDeliveryDateEligible: inlineEddEligible = false,
    timeWindow,
    events,
    userOwnership,
  } = useCurrentArticle() || {};
  const inlineEddCalculated = useAppSelector(selectCalculatedEdd);
  const { loading: locationLoading, fetchLocation } = useLocationByDpid(false);

  const isInternational = address?.internationalAddress ?? false;
  const addressType = address?.addressType;
  const locationName = address?.locationName;
  const dpid = address?.deliveryPointId;
  const isCollectionLocationSupported =
    addressType === AddressType.PARCEL_LOCKER || addressType === AddressType.PARCEL_COLLECT;
  const currentMilestone = events?.[0]?.milestone ?? Milestone.PENDING;
  const { start, end, passedWindow, deliveryDate } = timeWindow ?? {};
  const islocationLoading = locationLoading === ELoadingStatus.LOADING;
  const isLateAfterTimeWindow = (passedWindow && deliveryDate && !isToday(deliveryDate)) || false;

  const displayEddText = () => {
    if (isLateAfterTimeWindow && deliveryDate) {
      return getEddText(deliveryDate);
    }

    if (start && end) {
      const windowStart = formatTimeWindow(start);
      const windowEnd = formatTimeWindow(end);
      return `Today, ${windowStart}–${windowEnd}`;
    }

    const eddRangeText = getEddRangeText(eddRange?.fromDate, eddRange?.toDate);
    if (eddRangeText) return eddRangeText;

    const eddSingleText = deliveredByDateISO && getEddText(deliveredByDateISO);
    if (eddSingleText) return eddSingleText;

    if (currentMilestone === Milestone.COMING_TODAY) return 'Today';

    if (isInternational) return `Dates can’t be calculated`;

    return 'Dates calculated soon';
  };

  const displayEddTooltip = (() => {
    if (inlineEddCalculated) return tooltipContent.inlineEdd;
    if (isLateAfterTimeWindow) return tooltipContent.edd;
    if (timeWindow?.start && timeWindow?.end) return tooltipContent.timeWindow;
    if ((eddRange?.fromDate && eddRange?.toDate) || deliveredByDateISO) return tooltipContent.edd;
    if (isInternational) return tooltipContent.international;
    if (inlineEddEligible) return null;
    return tooltipContent.default;
  })();

  const displayEddIcon = (() => {
    if (!userOwnership) return <TruckIcon className={styles.eddIcon} />;
    if (addressType === AddressType.PARCEL_LOCKER) return <ParcelLockerIcon className={styles.eddIcon} />;
    if (addressType === AddressType.PARCEL_COLLECT) return <ParcelCollectIcon className={styles.eddIcon} />;
    return <TruckIcon className={styles.eddIcon} />;
  })();

  const displayLocationInfo = (() => {
    if (!isCollectionLocationSupported || !userOwnership || !locationName) return null;
    return (
      <>
        <Divider />
        <p className={clsx(styles.eddText, CommonClass.FULLSTORY_MASK)}>{locationName}</p>
        {dpid ? (
          <button
            className={styles.changeLocationButton}
            onClick={fetchLocation}
            aria-label={locationName && `View location details for ${locationName}`}
          >
            View location details
            {islocationLoading && (
              <Spinner
                variant='inline'
                colour='secondary'
                className={styles.inlineSpinner}
                data-testid='location-spinner'
              />
            )}
          </button>
        ) : null}
      </>
    );
  })();
  return (
    <div className={styles.eddWrapper} data-testid={TRACKING_PANEL_EDD_TEST_ID}>
      {displayEddIcon}
      <div className={styles.eddTextWrapper}>
        <p className={styles.eddSubtitle}>Arrival Details</p>
        <p className={styles.eddText} data-testid={TRACKING_PANEL_EDD_TEXT_TEST_ID}>
          {inlineEddEligible ? getCalculatedDeliveryDateText(inlineEddCalculated) : displayEddText()}
        </p>
        {inlineEddEligible && <InlineEdd />}
        {displayLocationInfo}
      </div>
      {!!displayEddTooltip && (
        <Infotip anchorClassName={styles.eddInfotipFloat} floatClassName={styles.eddInfotip}>
          {displayEddTooltip}
        </Infotip>
      )}
    </div>
  );
};

export default Edd;
